import { handleActions } from 'redux-actions';
import { Actions, receiveEcsListMessage, receiveEcsCallMessage, brokenVideoCall, receiveEcsCallIdMessage, sendEcsMSCallMessage, receiveEcsOfferMessage, receiveEcsRejectMessage, getVideoCallEventsRequest, getVideoCallEventsSuccess, getVideoCallEventsFailure, ecsCallsFetched, videoCallEnded, videoCallFailed, ecsListLoading } from 'actions/ecsActions';
import { Actions as SessionActions, logoutRequest } from 'actions/sessionActions';
import { VideoCall, CallId, VideoCallEvent, UtcTime } from 'shared/models';
import { Dictionary, omit, values, uniqBy } from 'lodash';

export type State = {
    videoCalls: Dictionary<VideoCall>;
    nextToken: string | null; // When non-null, there are more video calls available
    callIdByElevatorId: Dictionary<CallId>;
    lastUpdated: UtcTime;
    isLoading: boolean;
    callingElevator: {
        [groupId: string]: UtcTime; // time when the request was made
    };
    events: Dictionary<VideoCallEvent[]>; // Look up by callId
    eventsWithFrames: Dictionary<VideoCallEvent[]>; // Look up by callId
    isLoadingEvents: Dictionary<boolean>;
    eventsError: Error | null; // Just one error... not per call
};

export const initialState = Object.freeze<State>({
    videoCalls: {},
    nextToken: null,
    callIdByElevatorId: {},
    lastUpdated: 0,
    isLoading: false,
    callingElevator: {},
    events: {},
    eventsWithFrames: {},
    isLoadingEvents: {},
    eventsError: null,
});


function updateCall(state: State, videoCall: VideoCall): State {
    const { callId, elevatorId } = videoCall;

    const { videoCalls } = state;
    let { callIdByElevatorId } = state;
    const existingVideoCall = videoCalls[callId];
    if (existingVideoCall) {
        // Ignore the update if it's not newer than our existing information
        if (existingVideoCall.updated >= videoCall.updated) {
            return state;
        }
    }

    // Update the elevator-call lookup if necessary
    if (videoCall.ended && callIdByElevatorId[elevatorId] === callId) {
        // Disassociate the call from the elevator if it has just ended
        callIdByElevatorId = omit(callIdByElevatorId, elevatorId);
    }
    else if (!videoCall.ended) {
        // Associate the call with the device if it has not ended
        callIdByElevatorId = {
            ...callIdByElevatorId,
            [elevatorId]: callId,
        };
    }

    return {
        ...state,
        videoCalls: {
            ...videoCalls,
            [callId]: videoCall,
        },
        callIdByElevatorId,
    };
}

export const ecsReducer = handleActions({
    [Actions.ECS_LIST_LOADING]: (state: State, action: ReturnType<typeof ecsListLoading>) => {
        return {
            ...state,
            isLoading: true,
        };
    },
    [Actions.ECS_CALLS_FETCHED]: (state: State, action: ReturnType<typeof ecsCallsFetched>) => {
        const videoCalls = action.payload;
        return videoCalls.reduce((s, vc) => updateCall(s, vc), state);
    },
    [Actions.ECS_LIST_MSG]: (state: State, action: ReturnType<typeof receiveEcsListMessage>) => {
        const videoCalls = values(action.payload);
        const { nextToken } = action.meta;
        return videoCalls.reduce((s, vc) => updateCall(s, vc), {
            ...state,
            isLoading: false,
            nextToken,
        });
    },
    [Actions.ECS_CALL_MSG]: (state: State, action: ReturnType<typeof receiveEcsCallMessage>) => {
        const videoCall = action.payload;
        return updateCall(state, videoCall);
    },
    [Actions.ECS_CLID_MSG]: (state: State, action: ReturnType<typeof receiveEcsCallIdMessage>) => {
        const videoCall = action.payload;
        const { callId, elevatorId } = videoCall;
        const { videoCalls, callIdByElevatorId } = state;
        const existingVideoCall = videoCalls[callId];
        return {
            ...state,
            videoCalls:
                existingVideoCall && existingVideoCall.updated >= videoCall.updated
                    ? videoCalls
                    : {
                        ...videoCalls,
                        [callId]: videoCall,
                    },
            callIdByElevatorId: {
                ...callIdByElevatorId,
                [elevatorId]: callId,
            },
        };
    },
    [Actions.ECS_MSCL_MSG]: (state: State, action: ReturnType<typeof sendEcsMSCallMessage>) => {
        const elevatorId = action.payload;
        const { sent } = action.meta;
        const { callingElevator } = state;
        return {
            ...state,
            callingElevator: {
                ...callingElevator,
                [elevatorId]: sent,
            },
        };
    },
    [Actions.ECS_OFFR_MSG]: (state: State, action: ReturnType<typeof receiveEcsOfferMessage>) => {
        const callId = action.payload;
        const { callingElevator, callIdByElevatorId } = state;
        const elevatorId = Object.keys(callIdByElevatorId).find(did => callIdByElevatorId[did] === callId);
        if (!elevatorId) return state;
        return {
            ...state,
            callingElevator: omit(callingElevator, elevatorId),
        };
    },
    [Actions.ECS_RJCT_MSG]: (state: State, action: ReturnType<typeof receiveEcsRejectMessage>) => {
        const callId = action.payload;
        const { callingElevator, callIdByElevatorId } = state;
        const elevatorId = Object.keys(callIdByElevatorId).find(did => callIdByElevatorId[did] === callId);
        if (!elevatorId) return state;
        return {
            ...state,
            callingElevator: omit(callingElevator, elevatorId),
            callIdByElevatorId: omit(callIdByElevatorId, elevatorId),
        };
    },
    [Actions.BROKEN_VIDEO_CALL]: (state: State, action: ReturnType<typeof brokenVideoCall>) => {
        const { elevatorId, ended } = action.meta;
        let { videoCalls, callIdByElevatorId } = state;
        const callId = callIdByElevatorId[elevatorId];
        if (!callId) return state;
        const videoCall = videoCalls[callId];
        if (!videoCall) return state;

        if (!videoCall.ended) {
            videoCalls = {
                ...videoCalls,
                [callId]: {
                    ...videoCall,
                    ended,
                },
            };
        }

        if (callIdByElevatorId[videoCall.elevatorId] === callId) {
            callIdByElevatorId = omit(callIdByElevatorId, videoCall.elevatorId);
        }

        return {
            ...state,
            videoCalls,
            callIdByElevatorId,
        };
    },

    [Actions.VIDEO_CALL_ENDED]: (state: State, action: ReturnType<typeof videoCallEnded>) => {
        const callId = action.payload;
        const { when } = action.meta;
        const { callIdByElevatorId, callingElevator } = state;
        let { videoCalls } = state;
        if (!callId) return state;
        const videoCall = videoCalls[callId];
        if (!videoCall) return state;

        const { elevatorId } = videoCall;

        if (!videoCall.ended) {
            videoCalls = {
                ...videoCalls,
                [callId]: {
                    ...videoCall,
                    ended: when,
                },
            };
        }

        return {
            ...state,
            videoCalls,
            callIdByElevatorId: omit(callIdByElevatorId, elevatorId),
            callingElevator: omit(callingElevator, elevatorId),
        };
    },

    [Actions.VIDEO_CALL_FAILED]: (state: State, action: ReturnType<typeof videoCallFailed>) => {
        const callId = action.payload;
        const { videoCalls, callingElevator } = state;
        if (!callId) return state;
        const videoCall = videoCalls[callId];
        if (!videoCall) return state;
        const { elevatorId } = videoCall;
        return {
            ...state,
            callingElevator: omit(callingElevator, elevatorId),
        };
    },

    [Actions.GET_VC_EVENTS_REQUEST]: (state: State, action: ReturnType<typeof getVideoCallEventsRequest>) => {
        const callId = action.payload;
        const { isLoadingEvents } = state;
        return {
            ...state,
            isLoadingEvents: {
                ...isLoadingEvents,
                [callId]: true,
            },
            eventsError: null,
        };
    },
    [Actions.GET_VC_EVENTS_SUCCESS]: (state: State, action: ReturnType<typeof getVideoCallEventsSuccess>) => {
        const { callId, frames, more } = action.meta;
        const { events, eventsWithFrames, isLoadingEvents } = state;
        
        const callEvents =
            frames
                ? eventsWithFrames[callId]
                : events[callId];

        const thisCallEvents = {
            [callId]:
                callEvents
                    ? uniqBy([
                        ...callEvents,
                        ...action.payload,
                    ], ev => ev.id)
                    : action.payload,
        };

        return {
            ...state,
            events: frames ? events : {
                ...events,
                ...thisCallEvents,
            },
            eventsWithFrames: !frames ? eventsWithFrames : {
                ...eventsWithFrames,
                ...thisCallEvents,
            },
            isLoadingEvents: more ? isLoadingEvents : omit(isLoadingEvents, callId),
            eventsError: null,
        };
    },
    [Actions.GET_VC_EVENTS_FAILURE]: (state: State, action: ReturnType<typeof getVideoCallEventsFailure>) => {
        const { callId } = action.meta;
        const { isLoadingEvents } = state;
        return {
            ...state,
            isLoadingEvents: omit(isLoadingEvents, callId),
            eventsError: action.payload,
        };
    },


    //
    // Session Actions
    //
    [SessionActions.LOGOUT_REQUEST]: (state: State, action: ReturnType<typeof logoutRequest>) => {
        return initialState;
    },
} as any, initialState) as any;
